import React, { useState } from 'react'
import { Box, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { UserPlusIcon } from '@heroicons/react/24/outline'

import { Button } from '../../../../interaxDesignSystemComponents'
import { INTERAX_COLORS } from '../../../../constants/colors'
import { useTranslation } from '../../../../shared/hooks/useIntractionTranslate'
import { InviteUsersModal } from '../../components/InviteUsers/InviteUsersModal'
import { useWherebyRerun } from '../hooks/useWherebyRerun'
import { useUnseeAchievements } from '../hooks/useUnseeAchievements'
import { WherebyRerunTool } from './WherebyRerunTool'
import { UnseeAchievementsTool } from './UnseeAchievementsTool'

/**
 * Dev-mode-only debug panel. Owns the local "is tool X open" state and the
 * dev-only invite-users modal; sub-tools get their domain state from their
 * dedicated hooks (`useWherebyRerun`, `useUnseeAchievements`).
 */
export const ProfileDevToolsSection: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [showWhereby, setShowWhereby] = useState(false)
  const [showUnsee, setShowUnsee] = useState(false)
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false)

  const whereby = useWherebyRerun()
  const unsee = useUnseeAchievements()

  const onToggleWhereby = async () => {
    const next = !showWhereby
    setShowWhereby(next)
    if (next) await whereby.loadTeams()
  }

  const onToggleUnsee = async () => {
    const next = !showUnsee
    setShowUnsee(next)
    if (next) await unsee.loadUsers()
  }

  return (
    <Box
      sx={{
        padding: '24px',
        backgroundColor: INTERAX_COLORS.heritageBlue40,
        borderRadius: '12px',
        border: `1px solid ${INTERAX_COLORS.heritageBlue}`,
        marginBottom: '24px'
      }}
    >
      <Typography
        variant="h5"
        sx={{
          fontSize: '20px',
          fontWeight: 600,
          color: INTERAX_COLORS.white,
          marginBottom: '8px'
        }}
      >
        {t('facilitator.trainingSprint.profile.debugTools')}
      </Typography>
      <Typography sx={{ fontSize: '14px', color: INTERAX_COLORS.thunderGrey, marginBottom: '16px' }}>
        {t('gamer.accountSettings.debugToolsDescription')}
      </Typography>

      <Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
        <Button variant="primary" onClick={() => navigate('/onboarding')}>
          {t('facilitator.trainingSprint.profile.testOnboarding')}
        </Button>
        <Button variant="secondary" onClick={() => navigate('/facilitator/dev-tools')}>
          Dev Tools
        </Button>
        <Button
          variant="secondary"
          icon={<UserPlusIcon className="w-4 h-4" />}
          onClick={() => setIsInviteModalOpen(true)}
        >
          Invite users
        </Button>
        <Button variant="secondary" onClick={onToggleWhereby}>
          Whereby rerun
        </Button>
        <Button variant="secondary" onClick={onToggleUnsee}>
          Unsee achievements
        </Button>
      </Box>

      {(unsee.error || unsee.result) && (
        <Box sx={{ mt: 2 }}>
          {unsee.error && <Typography sx={{ fontSize: '14px', color: INTERAX_COLORS.sun }}>{unsee.error}</Typography>}
          {unsee.result && (
            <Typography sx={{ fontSize: '14px', color: INTERAX_COLORS.thunderGrey }}>{unsee.result}</Typography>
          )}
        </Box>
      )}

      {showWhereby && <WherebyRerunTool hook={whereby} />}
      {showUnsee && <UnseeAchievementsTool hook={unsee} />}

      <InviteUsersModal isOpen={isInviteModalOpen} onClose={() => setIsInviteModalOpen(false)} />
    </Box>
  )
}
